import type { SVGProps } from 'react';

import { Link, NavLink } from 'react-router-dom';

import {
  COMPANY_ADDRESS_LINE_1,
  COMPANY_ADDRESS_LINE_2,
  COMPANY_EMAIL,
  COMPANY_EMAIL_HREF,
  COMPANY_PHONE_DISPLAY,
  COMPANY_PHONE_TEL,
} from '../constants/companyContact';

/** Replace with live profiles when available. */
const SOCIAL = {
  instagram: 'https://www.instagram.com/',
  twitter: 'https://twitter.com/',
  linkedin: 'https://www.linkedin.com/',
  youtube: 'https://www.youtube.com/',
} as const;

const footerNav = ({ isActive }: { isActive: boolean }) =>
  `text-sm font-medium tracking-wide transition-colors duration-200 ${
    isActive ? 'text-[var(--home-line)]' : 'text-[var(--home-muted-on-dark)] hover:text-[var(--home-text-on-dark)]'
  }`;

const socialBtn =
  'inline-flex h-10 w-10 items-center justify-center rounded-lg text-[var(--home-muted-on-dark)] transition duration-200 hover:scale-110 hover:text-[var(--home-line)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--home-line)]';

function IconInstagram(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden {...props}>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4.25" />
      <circle cx="17.25" cy="6.75" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconX(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="currentColor" aria-hidden {...props}>
      <path d="M14.23 4h3.27l-7.14 8.16L18.5 20h-4.16l-5.02-5.85L5.45 20H2.16l7.64-8.73L2.5 4h4.27l4.53 5.28L14.23 4z" />
    </svg>
  );
}

function IconLinkedIn(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="currentColor" aria-hidden {...props}>
      <path d="M6.94 8.67H3.05V20h3.89V8.67zM4.99 3.5c-1.24 0-2 .84-2 1.88 0 1.02.75 1.85 1.91 1.85h.02c1.24 0 2-.83 2-1.85C6.92 4.34 6.18 3.5 4.99 3.5zM21 20h-3.89v-5.55c0-1.33-.02-3.05-1.86-3.05-1.86 0-2.14 1.45-2.14 2.95V20h-3.89V8.67h3.73v1.6h.05c.52-.98 1.78-2.02 3.67-2.02 3.93 0 4.65 2.58 4.65 5.93V20z" />
    </svg>
  );
}

function IconYouTube(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="currentColor" aria-hidden {...props}>
      <path d="M21.6 7.2s-.21-1.48-.86-2.13c-.77-.8-1.63-.8-2.03-.85C16.2 4 12 4 12 4s-4.2 0-6.71.22c-.4.05-1.26.05-2.03.85-.65.65-.86 2.13-.86 2.13S2.25 9.1 2.25 11v1.8c0 1.9.15 3.79.15 3.79s.21 1.48.86 2.13c.77.8 1.78.77 2.23.86 1.62.15 6.51.19 6.51.19s4.2 0 6.71-.22c.4-.05 1.26-.05 2.03-.85.65-.65.86-2.13.86-2.13s.15-1.9.15-3.79V11c0-1.9-.15-3.8-.15-3.8zM10 14.6V8.4l5.2 3.1-5.2 3.1z" />
    </svg>
  );
}

function IconMail(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden {...props}>
      <path d="M4 6h16v12H4V6z" />
      <path d="M4 7l8 6 8-6" />
    </svg>
  );
}

export function SiteFooter({ variant }: { variant: 'marketing' | 'admin' }) {
  if (variant === 'admin') {
    return (
      <footer
        id="site-footer"
        className="relative mt-8 scroll-mt-8 border-t border-white/10 bg-transparent px-4 py-10 text-sm text-[var(--home-muted-on-dark)] md:px-8"
      >
        <div className="mx-auto flex max-w-6xl flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="font-display font-medium text-[var(--home-text-on-dark)]">Breckenridge Capital</p>
            <p className="mt-2 text-xs text-[var(--home-muted-on-dark)]">Admin portal — operational use only.</p>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer
      id="site-footer"
      className="relative mt-24 scroll-mt-8 border-t border-white/10 bg-transparent px-4 py-12 text-[var(--home-muted-on-dark)] md:px-8 lg:px-10"
    >
      <div className="mx-auto grid max-w-[90rem] gap-10 md:grid-cols-[1fr_auto] md:gap-12 lg:grid-cols-[1.1fr_1fr_auto] lg:items-start">
        <div className="flex flex-col gap-6">
          <div>
            <p className="font-display text-base font-semibold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)]">
              Breckenridge Capital
            </p>
            <p className="mt-2 max-w-md font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)]">
              Institutional-quality commercial real estate for accredited investors — disciplined underwriting, transparent
              reporting.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href={SOCIAL.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className={socialBtn}
              aria-label="Breckenridge Capital on Instagram"
            >
              <IconInstagram />
            </a>
            <a
              href={SOCIAL.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className={socialBtn}
              aria-label="Breckenridge Capital on X (Twitter)"
            >
              <IconX />
            </a>
            <a
              href={SOCIAL.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className={socialBtn}
              aria-label="Breckenridge Capital on LinkedIn"
            >
              <IconLinkedIn />
            </a>
            <a
              href={SOCIAL.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className={socialBtn}
              aria-label="Breckenridge Capital on YouTube"
            >
              <IconYouTube />
            </a>
            <a href={COMPANY_EMAIL_HREF} className={socialBtn} aria-label="Email Breckenridge Capital">
              <IconMail />
            </a>
          </div>
        </div>

        <nav className="flex flex-col gap-4 md:items-end lg:items-center" aria-label="Footer">
          <div className="flex flex-wrap gap-x-8 gap-y-3 md:justify-end lg:justify-center">
            <NavLink to="/" end className={footerNav}>
              Home
            </NavLink>
            <NavLink to="/company" className={footerNav}>
              Company
            </NavLink>
            <NavLink to="/deals" className={footerNav}>
              Deals
            </NavLink>
            <NavLink to="/events" className={footerNav}>
              Events
            </NavLink>
            <NavLink to="/ira-401k" className={footerNav}>
              401k / IRA
            </NavLink>
            <NavLink to="/contact" className={footerNav}>
              Contact
            </NavLink>
          </div>
          <div className="flex flex-col gap-2 md:items-end lg:items-center">
            <a
              href={COMPANY_EMAIL_HREF}
              className="text-sm font-medium text-[var(--home-muted-on-dark)] transition-colors duration-200 hover:text-[var(--home-line)] md:text-right lg:text-center"
            >
              {COMPANY_EMAIL}
            </a>
            <a
              href={COMPANY_PHONE_TEL}
              className="text-sm font-medium text-[var(--home-muted-on-dark)] transition-colors duration-200 hover:text-[var(--home-line)] md:text-right lg:text-center"
            >
              {COMPANY_PHONE_DISPLAY}
            </a>
            <p className="max-w-xs text-sm leading-relaxed text-[var(--home-muted-on-dark)] md:text-right lg:text-center">
              {COMPANY_ADDRESS_LINE_1}
              <br />
              {COMPANY_ADDRESS_LINE_2}
            </p>
          </div>
        </nav>

        <div className="md:col-span-2 lg:col-span-1 lg:border-l lg:border-white/10 lg:pl-10">
          <p className="font-body text-xs leading-relaxed text-[var(--home-muted-on-dark)]">
            Investing involves risk, including loss of principal. Past performance does not indicate future results. Offerings
            may be conducted under Rule 506(c) where applicable — always review the PPM and consult your advisors. Any historical 
            returns, expected returns, or probability projections may not reflect actual future performance. While the data we
            use from third parties is believed to be reliable, we cannot ensure the accuracy or completeness of data provided by 
            investors or other third parties. Neither Cardone Capital nor any of its affiliates provide tax advice and do not
            represent in any manner that the outcomes described herein will result in any particular tax consequence. Offers to 
            sell, or solicitations of offers to buy, any security can only be made through official offering documents that contain 
            important information about investment objectives, risks, fees and expenses. Bitcoin is highly speculative and its 
            actual performance may not match investor expectation. Prospective investors should consult with a tax, legal and/or 
            financial adviser before making any investment decision. For additional important risks, disclosures, and information, 
            please visit https://breckenridge.capital/disclosures/
          </p>
          <p className="mt-6 font-mono text-[0.7rem] text-[var(--home-muted-on-dark)]/90">
            © {new Date().getFullYear()} Breckenridge Capital. All rights reserved.
          </p>
          <p className="mt-4">
            <Link
              to="/login"
              className="text-xs font-medium text-[var(--home-muted-on-dark)] transition-colors hover:text-[var(--home-line)]"
            >
              Client login
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
